import { Box, Center, Flex, Heading, Link, Spinner, Stack, Text, useToast } from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import React, { useContext, useEffect, useState } from 'react';
import { FaBookReader } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import config from '../config';
import ProfileContext, {
    getProfileErrorHeader,
    getProfileErrorText,
} from '../context/ProfileContext';
import SelectedGameContext from '../context/SelectedGameContext';
import AddProductForm from '../forms/AddProduct';
import { ItemDetail, ProductDetail } from '../interfaces/IInventory';
import InventoryDataCaller from '../util/inventoryDataCaller';

const initialValues: ProductDetail = {
    product_id: 'theme_classicDark',
    product_name: 'Classic Dark',
    product_desc: '',
    product_type: 'reward',
    cost: null,
    items: [
        {
            item_id: '6196834d-3c86-4bfb-b536-bf1ee821194b:theme_classicDark',
            quantity: 1,
            is_share: false,
            is_subscription: false,
            add_on_resubscribe: false,
            is_unique: false,
            max_age: null,
            expire_date: 0,
            item_def: {
                item_id: '6196834d-3c86-4bfb-b536-bf1ee821194b:theme_classicDark',
                item_name: 'Classic Dark',
                item_desc: '',
                item_type: 'theme',
                item_prop: null,
                item_owner: '6196834d-3c86-4bfb-b536-bf1ee821194b',
                creator_license: [
                    'd2eea033-f28d-41c6-a992-f85a55c70ab2',
                    '060d7fac-8605-4302-ace7-845afe8f9625',
                ],
                creator_user: null,
                ttl: 0,
            },
        },
    ],
    product_owner: '6196834d-3c86-4bfb-b536-bf1ee821194b',
    license: 'd2eea033-f28d-41c6-a992-f85a55c70ab2',
    ttl: 0,
};

type AddProductProps = {
    isSaved?: boolean;
};

export const AddProduct: React.FC<AddProductProps> = ({ isSaved = false }) => {
    const selectedGame = useContext(SelectedGameContext);
    const currLicense = selectedGame?.currLicense;
    const profile = useContext(ProfileContext);
    const [isEditDataLoaded, setIsEditDataLoaded] = useState(!isSaved);
    const [editData, setEditData] = useState<ProductDetail>(initialValues);
    const [items, setItems] = useState<Array<ItemDetail>>();
    const [itemsLoaded, setItemsLoaded] = useState(false);
    const toast = useToast();
    const { keycloak } = useKeycloak();
    const url = `${config.inventoryServiceURL}`;
    const { id } = useParams();
    const navigate = useNavigate();

    const renderError = () => (
        <Center width="100%" height="100%" display="flex" flexDirection="column">
            <Heading as="h3" size="md">
                {profile.errors.map((error) => getProfileErrorHeader(error))}
            </Heading>
            {profile.errors.map((error) => getProfileErrorText(error))}
        </Center>
    );

    const saveProduct = async (values: ProductDetail) => {
        setIsEditDataLoaded(false);
        const dataCaller = new InventoryDataCaller(url, keycloak.token!);
        const res = await dataCaller.updateProduct(values);
        if (res.status !== 200) {
            setIsEditDataLoaded(true);
            setEditData(values);
            return toast({
                title: 'There was an error',
                description: 'Please try creating/updating the product again.',
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        }
        setIsEditDataLoaded(true);
        toast({
            title: 'Success!',
            description: 'Product has been created. Cheers!',
            status: 'success',
            duration: 9000,
            isClosable: true,
        });
        navigate(`/game/${currLicense?.key}/inventory`);
    };

    // TODO: Move to separate util function
    const getOwners = async () => {
        try {
            const response = await fetch(
                `${config.licenseServiceURL}/license/${currLicense.key}/owners`,
                {
                    headers: {
                        Authorization: `Bearer ${keycloak.token}`,
                    },
                },
            );
            if (response.status === 200) {
                const data = await response.json();
                const ownersArray = data.data;
                return ownersArray;
            } else {
                return toast({
                    title: 'Unable to get information',
                    description: 'Unable to display list of license owners',
                    status: 'error',
                    duration: 9000,
                    variant: 'subtle',
                    isClosable: true,
                });
            }
        } catch (error) {
            console.log(error);
            return;
        }
    };

    useEffect(() => {
        if (!currLicense) {
            return;
        }

        const getItems = async () => {
            const owners = await getOwners();
            console.log('Owners: ', owners);
            const dc = new InventoryDataCaller(url, keycloak.token!);
            const data = await dc.getAllItems(owners.map((owner: any) => owner.id));
            console.log('data', data);
            setItems(data);
            // TODO add error handling
            // if (data.status !== 200) {
            //     setItemsLoaded(true);
            //     if (data.status === 404) {
            //         setItems([]);

            //         return;
            //     }
            //     return;
            // }

            // const result = await data.json();
            // const lbConfigArray = jsonToArray(result.data);

            // setItems(lbConfigArray);
            setItemsLoaded(true);
        };

        getItems();
    }, []);

    useEffect(() => {
        if (!currLicense) {
            return;
        }
        if (!id) {
            return;
        }
        const loadProduct = async (productID: string) => {
            const dataCaller = new InventoryDataCaller(url, keycloak.token!);
            const res = await dataCaller.getProduct(currLicense.key, productID);

            if (res.status !== 200) {
                setIsEditDataLoaded(true);
                if (res.status === 404) {
                    return;
                }
                toast({
                    title: 'Error',
                    description: res.message,
                    status: 'error',
                    duration: 9000,
                    isClosable: true,
                });
                return;
            }

            const { data: products } = await res.json();
            setEditData(products[0]);
            setIsEditDataLoaded(true);
        };

        loadProduct(id!);
    }, [currLicense, id]);

    const handleSubmit = (values: ProductDetail) => {
        saveProduct(values);
    };

    const displaySection = () => {
        if (!isEditDataLoaded) {
            return (
                <Center width="100%" height="100%">
                    <Spinner data-cy="loading" />
                </Center>
            );
        }
        return (
            <Box>
                <Flex justifyContent={'space-between'}>
                    <Heading>Add Product</Heading>
                    <Link
                        href={`${config.docsURL}/tutorials/leaderboard-configuration-manager`}
                        isExternal
                    >
                        <Flex alignItems={'center'}>
                            <Text mr={2}>Go to Documentation</Text>
                            <FaBookReader max={'2px'}></FaBookReader>
                        </Flex>
                    </Link>
                </Flex>
                <Stack spacing={10} direction="row">
                    {editData && (
                        <AddProductForm
                            initialData={editData}
                            items={items || []}
                            onSubmit={handleSubmit}
                            isSaved={isSaved}
                        />
                    )}
                </Stack>
            </Box>
        );
    };
    return (
        <Box>
            {profile.errors.length > 0 ? (
                <>
                    <Heading textAlign="center" marginBottom="1em">
                        Oh No! Error Detected
                    </Heading>
                    {renderError()}
                </>
            ) : isEditDataLoaded ? (
                displaySection()
            ) : (
                <Center width="100%" height="100%">
                    <Spinner data-cy="loading" />
                </Center>
            )}
            {!currLicense && profile.errors.length === 0 && (
                <Center width="100%" height="100%">
                    <Spinner data-cy="loading" />
                </Center>
            )}
        </Box>
    );
};
